import React, { useEffect, useState } from 'react';
import { db, auth } from '../firebase'; // Pastikan auth diimport dari firebase.js
import { collection, onSnapshot, doc, updateDoc, deleteDoc, query, orderBy } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { CheckCircle, Trash2, LogOut, MessageCircle, User, Calendar as CalIcon } from 'lucide-react';
import Swal from 'sweetalert2';

const AdminPanel = () => {
  const [reservasi, setReservasi] = useState([]);

  // 1. Ambil data dengan urutan terbaru (berdasarkan timestamp)
  useEffect(() => {
    const q = query(collection(db, "reservasi"), orderBy("timestamp", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setReservasi(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsubscribe();
  }, []);

  // 2. Fungsi Logout
  const handleLogout = () => {
    Swal.fire({
      title: 'Keluar Admin?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#111827',
      cancelButtonColor: '#dc2626',
      confirmButtonText: 'Ya, Logout'
    }).then((result) => {
      if (result.isConfirmed) {
        signOut(auth);
      }
    });
  };

  // 3. Konfirmasi Pembayaran/Booking
  const handleConfirm = async (id) => {
    const result = await Swal.fire({
      title: 'Konfirmasi Reservasi?',
      text: "Status akan berubah menjadi Confirmed dan tanggal akan terblokir.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Ya, Konfirmasi!'
    });

    if (result.isConfirmed) {
      try {
        const docRef = doc(db, "reservasi", id);
        await updateDoc(docRef, { status: 'confirmed' });
        Swal.fire('Berhasil!', 'Pembayaran telah dikonfirmasi.', 'success');
      } catch (error) {
        Swal.fire('Error', 'Gagal memperbarui status.', 'error');
      }
    }
  };

  // 4. Hapus Reservasi
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Hapus Data?',
      text: "Data yang dihapus tidak dapat dikembalikan.",
      icon: 'error',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Hapus Permanen'
    });

    if (result.isConfirmed) {
      await deleteDoc(doc(db, "reservasi", id));
      Swal.fire('Dihapus!', 'Data telah dibersihkan dari sistem.', 'success');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 font-poppins pb-20">
      {/* Header Admin */}
      <nav className="bg-white shadow-sm px-6 py-4 mb-8 flex justify-between items-center border-b border-gray-100">
        <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-red-600 rounded flex items-center justify-center text-white font-bold">B</div>
            <h1 className="text-xl font-serif font-bold text-gray-800 hidden sm:block">Admin Panel <span className="text-red-600 italic">Difa BallaEja</span></h1>
        </div>
        <button 
          onClick={handleLogout}
          className="flex items-center space-x-2 bg-gray-100 hover:bg-red-50 hover:text-red-600 px-4 py-2 rounded-lg transition-all text-sm font-bold text-gray-600"
        >
          <LogOut size={16} />
          <span>LOGOUT</span>
        </button>
      </nav>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <h2 className="text-2xl font-bold text-gray-800">Daftar Reservasi</h2>
            <div className="bg-white px-4 py-2 rounded-lg shadow-sm border border-gray-100 text-xs font-medium text-gray-500">
                Total: {reservasi.length} Pesanan
            </div>
        </div>

        {/* Tabel Responsif */}
        <div className="bg-white shadow-2xl rounded-xl overflow-hidden border border-gray-100">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead className="bg-gray-900 text-white uppercase text-[10px] tracking-[0.2em]">
                <tr>
                  <th className="p-5">Tamu & Kontak</th>
                  <th className="p-5">Check In - Out</th>
                  <th className="p-5">Jumlah Tamu</th>
                  <th className="p-5">Status</th>
                  <th className="p-5 text-center">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {reservasi.map((res) => (
                  <tr key={res.id} className="hover:bg-gray-50 transition-colors">
                    <td className="p-5">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-red-50 text-red-600 rounded-full flex items-center justify-center">
                            <User size={20} />
                        </div>
                        <div>
                            <div className="font-bold text-gray-800 uppercase text-sm">{res.nama}</div>
                            <a 
                                href={`https://wa.me/${res.whatsapp}`} 
                                target="_blank" 
                                rel="noreferrer"
                                className="text-xs text-green-600 flex items-center hover:underline"
                            >
                                <MessageCircle size={12} className="mr-1" /> {res.whatsapp}
                            </a>
                        </div>
                      </div>
                    </td>
                    <td className="p-5 text-sm text-gray-600">
                      <div className="flex items-center space-x-2 font-medium">
                        <CalIcon size={14} className="text-gray-400" />
                        <span>{res.checkIn} — {res.checkOut}</span>
                      </div>
                    </td>
                    <td className="p-5">
                        <span className="text-sm font-semibold text-gray-700 bg-gray-100 px-3 py-1 rounded-md">
                            {res.guests || '10-15'} Orang
                        </span>
                    </td>
                    <td className="p-5">
                      <span className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest ${
                        res.status === 'confirmed' 
                        ? 'bg-green-100 text-green-700 border border-green-200' 
                        : 'bg-yellow-100 text-yellow-700 border border-yellow-200 animate-pulse'
                      }`}>
                        {res.status || 'pending'}
                      </span>
                    </td>
                    <td className="p-5">
                      <div className="flex justify-center space-x-2">
                        {res.status !== 'confirmed' && (
                          <button 
                            onClick={() => handleConfirm(res.id)} 
                            className="p-2.5 bg-green-500 text-white rounded-lg hover:bg-green-600 hover:shadow-lg transition-all"
                            title="Konfirmasi Pembayaran"
                          >
                            <CheckCircle size={18} />
                          </button>
                        )}
                        <button 
                          onClick={() => handleDelete(res.id)} 
                          className="p-2.5 bg-white text-red-500 border border-red-100 rounded-lg hover:bg-red-500 hover:text-white transition-all shadow-sm"
                          title="Hapus Reservasi"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                
                {reservasi.length === 0 && (
                  <tr>
                    <td colSpan="5" className="p-10 text-center text-gray-400 italic">
                      Belum ada data reservasi yang masuk...
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;